
import {
  Drawer,
  Button,
  Form,
  Input,
  FormItem,
  Select,
  Option,
  OptionGroup,
} from 'element-ui';
import GoogleAutoComplete from '@/components/GoogleAutoComplete.vue';
import addressMixin from '@/mixins/addressMixin';
import { MapPinIcon } from 'vue-feather-icons';
import { AddressHelpers } from '@/helpers/AddressHelpers';

export default {
  model: {
    prop: 'address',
    event: 'input',
  },
  components: {
    Drawer,
    Button,
    Form,
    Input,
    FormItem,
    Select,
    Option,
    OptionGroup,
    GoogleAutoComplete,
    MapPinIcon,
  },
  mixins: [addressMixin],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    address: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      toggleDrawer: this.visible,
      direction: 'ltr',
      title: this.$t('Address'),
      addressInfo: this.getInitialAddressInfo(),
      checkoutError: '',
    };
  },
  computed: {
    countries() {
      return this.$localisation.countries;
    },
    getClientCountry() {
      return this.countries.find(
        (c) => c.CountryIsoCode === this.clientCountry?.country
      );
    },
    rules() {
      const required = (msg) => ({
        required: true,
        message: this.$t(msg || 'Required Field'),
        trigger: 'blur',
      });
      return {
        Address1: [required()],
        Postcode: [required()],
        City: [required()],
        CountryIsoCode: [{ ...required(), trigger: ['blur', 'change'] }],
      };
    },
  },
  watch: {
    address: {
      handler() {
        this.syncAddressInfo();
      },
      deep: true,
    },
    addressInfo: {
      handler() {
        if (!this.checkoutError) return;
        if (!this.hasMissingRequiredAddressFields()) {
          this.checkoutError = '';
          this.$emit('onValidationClear');
        }
      },
      deep: true,
    },
    'addressInfo.CountryIsoCode': {
      handler(val) {
        if (!val) {
          this.addressInfo.Country = '';
          return;
        }
        const found = this.countries.find((c) => c.CountryIsoCode === val);
        if (found) {
          this.addressInfo.Country = found.CountryName;
        }
      },
      immediate: true,
    },
    toggleDrawer(val) {
      if (!val) return;
      setTimeout(() => {
        this.focusGoogleAddressInput();
      }, 300);
    },
  },
  created() {},
  async mounted() {
    this.syncAddressInfo();
    await this.loadClientCountryIsoCode();

    const bus = (ev, cb) => this.$root.$on(ev, cb);
    bus('checkout:validate', this.validateForCheckout);
    bus('checkout:clear-validate', this.clearCheckoutValidation);
  },
  beforeDestroy() {
    this.$root.$off('checkout:validate', this.validateForCheckout);
    this.$root.$off('checkout:clear-validate', this.clearCheckoutValidation);
  },
  methods: {
    focusGoogleAddressInput() {
      this.$nextTick(() => {
        setTimeout(() => {
          const inputEl =
            this.$refs.addressGoogleInput?.$refs?.addressInput?.$el;
          if (inputEl && typeof inputEl.focus === 'function') {
            inputEl.focus();
          }
        }, 200);
      });
    },
    hasMissingRequiredAddressFields() {
      const requiredFields = ['Address1', 'City', 'Postcode', 'CountryIsoCode'];
      return requiredFields.some((field) => {
        const value = this.addressInfo?.[field];
        return !value || !String(value).trim();
      });
    },
    validateForCheckout() {
      return new Promise((resolve) => {
        if (!this.$refs.AddressForm) {
          if (this.hasMissingRequiredAddressFields()) {
            this.checkoutError = this.$t(
              'Please complete delivery address fields.'
            );
            this.$emit('onValidationError', this.checkoutError);
            resolve(false);
            return;
          }

          this.checkoutError = '';
          this.$emit('onValidationClear');
          resolve(true);
          return;
        }

        this.$refs.AddressForm.validate((valid) => {
          if (valid) {
            this.checkoutError = '';
            this.$emit('onValidationClear');
            resolve(true);
            return;
          }

          this.checkoutError = this.$t(
            'Please complete delivery address fields.'
          );
          this.$emit('onValidationError', this.checkoutError);
          resolve(false);
        });
      });
    },
    clearCheckoutValidation() {
      this.checkoutError = '';
      this.$refs.AddressForm?.clearValidate();
      this.$emit('onValidationClear');
    },
    syncAddressInfo() {
      if (!this.address) return;
      this.addressInfo.Address1 = this.address.Address1;
      this.addressInfo.City = this.address.City;
      this.addressInfo.Postcode = this.address.Postcode;
      this.addressInfo.StateName = this.address.StateName;
      this.addressInfo.StateCode = this.address.StateCode;
      this.addressInfo.StateIsoCode = this.address.StateIsoCode;
      this.addressInfo.CountryIsoCode = this.address.CountryIsoCode;
      this.addressInfo.Country = this.address.Country;
      this.addressInfo.Company = this.address.Company;
    },
    getInitialAddressInfo() {
      return {
        Address1: '',
        Postcode: '',
        City: '',
        StateName: '',
        StateCode: '',
        StateIsoCode: '',
        CountryIsoCode: '',
        Country: '',
        Company: '',
      };
    },
    onAddressTextChange(e) {
      this.addressInfo.Address1 = e.text;
    },
    onStateNameChange() {
      this.addressInfo.StateCode = '';
      this.addressInfo.StateIsoCode = '';
    },

    async loadClientCountryIsoCode() {
      try {
        const res = await this.$store.dispatch(
          'shop/localisation/fetchVisitor'
        );
        this.clientCountry = res;
        // Default to detected country if not set
        if (!this.addressInfo.CountryIsoCode && res?.country) {
          this.addressInfo.CountryIsoCode = res.country;
        }
      } catch (e) {}
    },
    mapGoogleAddress(ev) {
      if (!ev) return;
      this.addressInfo.Address1 = ev.courtAddress;
      this.addressInfo.City = ev.city || '';
      this.addressInfo.Postcode = ev.postalCode || '';
      this.addressInfo.StateName = ev.StateName || '';
      this.addressInfo.StateCode = ev.StateCode || '';
      this.addressInfo.StateIsoCode = ev.StateIsoCode || '';

      if (ev.country) {
        const found = this.countries.find(
          (c) => c.CountryName?.toLowerCase() === ev.country?.toLowerCase()
        );
        if (found) {
          this.addressInfo.CountryIsoCode = found.CountryIsoCode;
          this.addressInfo.Country = found.CountryName;
        }
      }
    },
    onSelectAddress(ev) {
      this.mapGoogleAddress(ev);
    },

    async submitForm() {
      const valid = await this.validateForCheckout();
      if (!valid) return;

      const result = { ...this.address, ...this.addressInfo };
      this.checkoutError = '';
      this.$emit('onValidationClear');
      this.$emit('onSubmit', result);
      this.$emit('input', result);
      this.toggleDrawer = false;
    },
  },
};
